import React, { memo } from 'react';

import { STALE_TIME } from '@blockworks/platform/api';
import type { GetDashboardResponse } from '@blockworks/platform/api/research/studio';
import { studioExecutionApi, StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { Spinner, Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';
import { cn } from '@blockworks/ui/system';

import { useVisualizationOptionsContext } from '../../../../context';
import { getTableVisualizationPageSize } from '../../utils';
import { useIsVisible } from '../../utils/use-is-visible';
import { VisualizationRenderer } from '../visualization-renderer';
import { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

type PreloadedVisualizationProps = {
    visualization?: GetDashboardResponse['data']['visualizations'][number];
} & Pick<VisualizationRendererProps, 'componentSlots'>;

interface PreloadedVisualizationComponent extends React.NamedExoticComponent<PreloadedVisualizationProps> {}

const PreloadedVisualization = memo(
    function PreloadedVisualization({ visualization, componentSlots }: PreloadedVisualizationProps) {
        const { containerRef, isViewable, wasViewable } = useIsVisible();
        const { features } = useVisualizationOptionsContext();
        const { data, isFetching, isPending } = studioExecutionApi.useGetExecution({
            variables: {
                id: visualization?.lastExecutionId as string,
                limit: getTableVisualizationPageSize(visualization?.config),
                sameOriginProxy: features?.dangerouslyProxyRequests,
            },
            enabled: !!visualization?.lastExecutionId,
            staleTime: STALE_TIME.$1Hour,
            refetchOnWindowFocus: false,
        });

        let content: React.ReactNode = null;

        const isHidden = visualization?.type === StudioVisualizationType.Table ? !wasViewable : !isViewable;

        if (isPending || isFetching) {
            content = <Spinner size={10} color="purple" />;
        }

        const { _meta, data: execution } = data ?? {};
        if (!data || isHidden) {
            content = null;
        } else if (!_meta?.success || !visualization?.type || !execution || !visualization?.lastExecutionId) {
            console.error('Visualization not found.', {
                hasVisualizationType: !!visualization?.type,
                hasExecution: !!execution,
                data,
            });

            const { HeaderEnd } = componentSlots ?? {};

            content = (
                <>
                    {!!HeaderEnd && (
                        <Bit.div pos="absolute" top={2} right={2}>
                            <HeaderEnd removeOnly />
                        </Bit.div>
                    )}
                    <Bit.div display="flex" justifyContent="center" alignItems="center" w="full" h="full">
                        <Text center>Visualization not found.</Text>
                    </Bit.div>
                </>
            );
        } else {
            content = (
                <VisualizationRenderer
                    componentSlots={componentSlots}
                    visualization={{ ...visualization, execution }}
                />
            );
        }

        return (
            <Bit.div
                ref={containerRef}
                w="full"
                h="full"
                justifyContent="center"
                alignItems="center"
                pos="relative"
                className={cn('scroller', { invisible: !isViewable })}
            >
                {content}
            </Bit.div>
        );
    },
    (prev, next) => prev.visualization?.id === next.visualization?.id,
) as PreloadedVisualizationComponent;

export { PreloadedVisualization };
